import React, { useMemo, useRef, useState } from 'react';

import { Select } from '@aline/common';
import { InputAdornment, InputBase, Paper, Popper, Typography } from '@mui/material';
import type { AutocompleteChangeReason } from '@mui/material/Autocomplete';
import Autocomplete from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import { useAsync, useClickAway } from 'react-use';
import { AlineAvatar, AlineCheckbox } from 'src/components';
import { MagnifyingGlassIcon } from 'src/components/icons/MagnifyingGlassIcon';
import { Apollo } from 'src/gql/generated';
import { useGetOrgMembers } from 'src/hooks';

type UnassignedOption = { __typename: 'Unassigned' };
type UserOption = Pick<Apollo.User, '__typename' | 'id' | 'displayName' | 'avatarColor'>;

export type OrgMemberOption = UserOption | UnassignedOption;

type OrgMemberAutocompleteValue = OrgMemberOption | null;

// Styled components replacing makeStyles
const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.tooltip,
}));

const StyledPaper = styled(Paper)(() => ({
  borderRadius: 3,
  width: 320,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`, // Updated to use MUI v5 palette
  boxSizing: 'border-box',
  padding: theme.spacing(1),
}));

const OptionContainer = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  width: '100%',
}));

interface OrgMemberPopperProps {
  anchorEl: HTMLDivElement | null;
  onChange: (
    event: React.SyntheticEvent,
    value: OrgMemberAutocompleteValue,
    reason: AutocompleteChangeReason
  ) => void;
  onClose: () => void;
  open: boolean;
  selectedAssignees: OrgMemberOption[];
  className?: string;
  dataCy?: string;
  disabled?: boolean;
  getIsOptionDisabled?: (option: OrgMemberOption) => boolean;
  includeUnassignedOpt?: boolean;
  placeholder?: string;
}

export function OrgMemberPopper(props: OrgMemberPopperProps): JSX.Element | null {
  const {
    anchorEl,
    className,
    dataCy,
    disabled,
    getIsOptionDisabled,
    includeUnassignedOpt = true,
    onChange,
    onClose,
    open,
    placeholder = 'Search org members...',
    selectedAssignees,
  } = props;

  const anchorRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const {
    loading,
    orgMembers,
    refetch: refetchOrgMembers,
  } = useGetOrgMembers({ variables: { filter: Apollo.OrgMemberFilter.Active } });
  const selectedAssigneesSet = new Set(
    selectedAssignees.map(option => getOrgOptionUniqIdentifier(option))
  );
  const options = useMemo((): OrgMemberOption[] => {
    return [...orgMembers].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [orgMembers]);

  // Close selector on click away.
  useClickAway(anchorRef, () => onClose());

  useAsync(async () => {
    if (open) await refetchOrgMembers();
  }, [open]);

  return (
    <StyledPopper anchorEl={anchorEl} open={open} placement="bottom-start">
      <StyledPaper ref={anchorRef}>
        <Autocomplete
          disableCloseOnSelect
          disablePortal
          open
          blurOnSelect={false}
          className={className}
          clearOnBlur={false}
          data-cy={dataCy}
          disabled={disabled}
          getOptionDisabled={getIsOptionDisabled}
          getOptionKey={option => getOrgOptionUniqIdentifier(option)}
          getOptionLabel={option => getOrgOptionDisplayString(option)}
          inputValue={inputValue}
          isOptionEqualToValue={(option, value) => {
            const valueId = getOrgOptionUniqIdentifier(value);
            const optionId = getOrgOptionUniqIdentifier(option);
            return optionId === valueId;
          }}
          loading={loading}
          options={
            includeUnassignedOpt
              ? [{ __typename: 'Unassigned' } satisfies UnassignedOption, ...options]
              : options
          }
          renderInput={params => {
            const { slotProps, ...rest } = params;
            const { startAdornment: _ignoredStart, ...otherInputProps } = slotProps.input;

            return (
              <StyledInputBase
                autoFocus
                inputProps={slotProps.htmlInput}
                // Wire Autocomplete to the actual input element
                inputRef={slotProps.input.ref}
                placeholder={placeholder}
                startAdornment={
                  <InputAdornment position="start">
                    <MagnifyingGlassIcon />
                  </InputAdornment>
                }
                {...rest}
                {...otherInputProps}
              />
            );
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;

            return (
              <li key={key} {...optionProps}>
                <OrgMemberOption
                  option={option}
                  selected={selectedAssigneesSet.has(getOrgOptionUniqIdentifier(option))}
                />
              </li>
            );
          }}
          slotProps={{
            listbox: {
              'data-cy': Select.components.assigneesAutocomplete.listBox,
            },
          }}
          sx={{
            '& .MuiAutocomplete-option': { p: 1 },
            '& .MuiPaper-root': { boxShadow: 'unset', m: 0 },
            '& .MuiPopper-root': { position: 'static' },
          }}
          value={null}
          onChange={onChange}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') return;
            setInputValue(value);
          }}
        />
      </StyledPaper>
    </StyledPopper>
  );
}

function OrgMemberOption({
  option,
  selected,
}: {
  option: OrgMemberOption;
  selected: boolean;
}): JSX.Element {
  return (
    <OptionContainer>
      <AlineCheckbox checked={selected} />
      <AlineAvatar avatarInfo={option.__typename === 'User' ? option : null} />
      <Typography noWrap>{getOrgOptionDisplayString(option)}</Typography>
    </OptionContainer>
  );
}

export function getOrgOptionUniqIdentifier(option: OrgMemberOption): string {
  return option.__typename === 'User' ? option.id : 'Unassigned';
}

export function getOrgOptionDisplayString(option: OrgMemberOption): string {
  return option.__typename === 'User' ? option.displayName : 'Unassigned';
}
